<template>
  <div v-if="!canalId">
    <v-card
      class="d-flex align-center justify-center"
      height="150"
      outlined
      flat
    >
      <span class="title font-weight-semibold">
        Esta seção só será liberada após criar o canal.
      </span>
    </v-card>
  </div>

  <v-card
    v-else
    outlined
  >
    <v-card-title class="justify-center">
      <v-icon class="mr-2">
        {{ icons.mdiEmailLock }}
      </v-icon>

      <span class="text-h6 font-weight-medium">Exceções de e-mail</span>
    </v-card-title>

    <v-card-text class="pa-3">
      <v-form
        ref="formRef"
        class="d-flex align-center"
        @submit.prevent="adicionarEmail"
      >
        <v-text-field
          v-model="email"
          dense
          outlined
          label="Cadastrar e-mail na blacklist"
          validate-on-blur
          :rules="[validators.required, validators.emailValidator]"
          :disabled="isAddingEmail"
        />

        <v-btn
          fab
          class="ml-2 mb-1"
          :loading="isAddingEmail"
        >
          <v-icon
            size="35"
            color="primary"
            @click="adicionarEmail"
          >
            {{ icons.mdiPlus }}
          </v-icon>
        </v-btn>
      </v-form>

      <v-row>
        <v-col
          v-if="isGetBlacklistLoading"
          cols="12"
        >
          <div style="min-height: 4px">
            <v-progress-linear indeterminate />
          </div>
        </v-col>

        <v-col cols="12">
          <v-data-table
            hide-default-header
            hide-default-footer
            class="elevation-4"
            :headers="headers"
            :items="blacklist"
            no-data-text="Nenhum item na blacklist"
          >
            <template #item.email="{ item }">
              <v-chip
                color="primary"
                outlined
              >
                {{ item.email }}
              </v-chip>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-end">
                <v-icon
                  color="error"
                  @click="selecionarEmailParaRemocao(item)"
                >
                  {{ icons.mdiDelete }}
                </v-icon>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-dialog
      v-model="isDialogOpen"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          Remover e-mail da blacklist
        </v-card-title>

        <v-card-text>
          Tem certeza que deseja remover <strong>{{ emailSelecionado.email }}</strong> da blacklist?
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="secondary"
            outlined
            :disabled="isDeletingEmail"
            @click="isDialogOpen = false"
          >
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            :loading="isDeletingEmail"
            @click="confirmarExlusao"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, watch } from '@vue/composition-api'
import { mdiEmailLock, mdiPlus, mdiDelete } from '@mdi/js'
import { emailValidator, required } from '@core/utils/validation'
import CanalService from '@/services/modules/CanalService'
import useDialog from '@/composables/useDialog'

export default {
  props: {
    canalId: {
      type: Number,
      default: null,
    },
  },

  setup(props, { emit }) {
    const email = ref('')
    const blacklist = ref([])
    const emailSelecionado = ref({})

    const { formRef, resetForm, validateForm } = useDialog(emit, 'isDialogOpen')

    const isDialogOpen = ref(false)
    const isDeletingEmail = ref(false)
    const isAddingEmail = ref(false)
    const isGetBlacklistLoading = ref(false)

    const headers = [
      { text: 'E-mail', value: 'email' },
      { text: 'Actions', value: 'actions' },
    ]

    async function getBlackList() {
      if (!props.canalId) return

      try {
        isGetBlacklistLoading.value = true
        blacklist.value = await CanalService.getCanalEmailBlacklist(props.canalId)
      } finally {
        isGetBlacklistLoading.value = false
      }
    }

    watch(
      () => props.canalId,
      v => {
        if (v) {
          getBlackList()
        } else {
          blacklist.value = []
        }
      },
      { immediate: true },
    )

    function selecionarEmailParaRemocao(item) {
      emailSelecionado.value = { ...item }
      isDialogOpen.value = true
    }

    async function confirmarExlusao() {
      try {
        isDeletingEmail.value = true
        await CanalService.deletarCanalEmailBlacklist(props.canalId, { data: emailSelecionado.value })
        isDialogOpen.value = false
        await getBlackList()
      } finally {
        isDeletingEmail.value = false
      }
    }

    async function adicionarEmail() {
      if (!validateForm()) {
        return
      }

      try {
        isAddingEmail.value = true
        await CanalService.criarCanalEmailBlacklist(props.canalId, { email: email.value })
        resetForm()
        await getBlackList()
      } finally {
        isAddingEmail.value = false
      }
    }

    return {
      email,
      blacklist,
      emailSelecionado,

      isDeletingEmail,
      isAddingEmail,
      isGetBlacklistLoading,

      isDialogOpen,
      formRef,

      headers,

      adicionarEmail,
      selecionarEmailParaRemocao,
      confirmarExlusao,

      validators: {
        required,
        emailValidator,
      },
      icons: {
        mdiEmailLock,
        mdiPlus,
        mdiDelete,
      },
    }
  },
}
</script>
